/**
 * External site-resource URLs for a managed property. Pure: URL builders
 * over the navigation ids stored on `property_directory` (customer portal
 * reads them through `property_directory_customer_view`, admin reads the
 * table). Both Site Resources surfaces build their link rows from
 * `directoryLinks()` so a URL scheme changes in exactly one place.
 */

export interface AdditionalNetwork {
  name: string;
  mission_control_site_id: string | null;
  unifi_host_id: string | null;
}

/** The directory columns `directoryLinks()` reads. */
export interface PropertyLinkSource {
  mission_control_site_id: string | null;
  unifi_host_id: string | null;
  auvik_domain_prefix: string | null;
  rankings_url: string | null;
  learn_url: string | null;
  engineering_url: string | null;
  ordering_url: string | null;
  communications_vendor_url: string | null;
  additional_networks: AdditionalNetwork[];
}

export interface PropertyLinks {
  missionControl: string | null;
  unifi: string | null;
  auvik: string | null;
  rankings: string | null;
  learn: string | null;
  engineering: string | null;
  ordering: string | null;
  communicationsVendor: string | null;
  additionalNetworks: Array<{ name: string; missionControl: string | null; unifi: string | null }>;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function stringOrNull(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value : null;
}

/** Runtime guard for the JSONB `additional_networks` column. */
export function parseAdditionalNetworks(json: unknown): AdditionalNetwork[] {
  if (!Array.isArray(json)) return [];
  const out: AdditionalNetwork[] = [];
  for (const item of json) {
    if (!isRecord(item)) continue;
    const name = stringOrNull(item.name);
    if (!name) continue;
    out.push({
      name,
      mission_control_site_id: stringOrNull(item.mission_control_site_id),
      unifi_host_id: stringOrNull(item.unifi_host_id),
    });
  }
  return out;
}

export function missionControlUrl(siteId: string | null): string | null {
  return siteId ? `https://monitor.paznetworks.com/site/${encodeURIComponent(siteId)}` : null;
}

export function unifiConsoleUrl(hostId: string | null): string | null {
  return hostId ? `https://unifi.ui.com/consoles/${hostId}/network/default/dashboard` : null;
}

export function auvikUrl(domainPrefix: string | null): string | null {
  if (!domainPrefix) return null;
  const prefix = domainPrefix.trim().toLowerCase();
  return /^[a-z0-9-]+$/.test(prefix) ? `https://${prefix}.us6.my.auvik.com/` : null;
}

export function directoryLinks(entry: PropertyLinkSource | null | undefined): PropertyLinks {
  if (!entry) {
    return {
      missionControl: null,
      unifi: null,
      auvik: null,
      rankings: null,
      learn: null,
      engineering: null,
      ordering: null,
      communicationsVendor: null,
      additionalNetworks: [],
    };
  }
  return {
    missionControl: missionControlUrl(entry.mission_control_site_id),
    unifi: unifiConsoleUrl(entry.unifi_host_id),
    auvik: auvikUrl(entry.auvik_domain_prefix),
    rankings: entry.rankings_url,
    learn: entry.learn_url,
    engineering: entry.engineering_url,
    ordering: entry.ordering_url,
    communicationsVendor: entry.communications_vendor_url,
    additionalNetworks: entry.additional_networks.map((n) => ({
      name: n.name,
      missionControl: missionControlUrl(n.mission_control_site_id),
      unifi: unifiConsoleUrl(n.unifi_host_id),
    })),
  };
}
